import {
  EARLIEST_READY_TIME_COLLECTION_ERROR,
  EDIT_PLAN_SATURN_AUTO,
  INVALID_TIME_FORMAT,
  LATEST_READY_TIME_COLLECTION_ERROR,
} from "../../../consts/strings";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { memo, useEffect, useState } from "react";

import { COLLECTION_FIELDS } from "../../../consts/collections";
import InputMask from "react-input-mask";
import { collectionEditPostcodeSelector } from "../../../reducers/ownApp/selectors";
import { getPlanCollectionDefaults } from "../../../reducers/matrixProfile/selectors";
import { isHHMMFormat } from "../../../utils/date";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";

const useStyles = makeStyles({
  bold: {
    fontWeight: "bold",
  },
  container: {
    overflow: "hidden",
  },
});

export const EditCollectionsModalContent = memo(
  ({ collection, handleChange, handleInputError }) => {
    const [values, setValues] = useState(collection);
    const [inputErrors, setInputErrors] = useState({});
    const styles = useStyles();
    const collectionEditPostcode = useSelector(collectionEditPostcodeSelector);
    const collectionDefaults = useSelector(getPlanCollectionDefaults);

    useEffect(() => {
      let inputErrorFields = {};
      COLLECTION_FIELDS.forEach((field) => {
        inputErrorFields[field.valueKey] = false;
      });
      setInputErrors(inputErrorFields);
    }, []);

    useEffect(() => {
      const fields = Object.keys(inputErrors).map((key) => inputErrors[key]);
      const isErrorPresent = fields.includes(true);
      handleInputError(isErrorPresent);
    }, [handleInputError, inputErrors]);

    useEffect(() => {
      handleChange && handleChange(values);
    }, [handleChange, values]);

    const onChange = (event) => {
      setValues({ ...values, [event.target.name]: event.target.value });
    };

    const isValidInputFormat = (valueKey, type) => {
      const value = values[valueKey];
      if (type === "text") return isHHMMFormat(value);
      else if (type === "bool") return typeof value === "boolean";
      else return false;
    };

    const validateTimeRange = (valueKey) => {
      const createComparableDate = (time, hoursModifier) => {
        if (typeof time === "string") {
          const splitTime = time.split(":");
          let hours = parseInt(splitTime[0]);
          if (hoursModifier && typeof hoursModifier === "number") {
            hours += hoursModifier;
          }
          const mins = parseInt(splitTime[1]);
          const comparableDate = new Date().setHours(hours, mins, 0, 0);
          return comparableDate;
        } else {
          return null;
        }
      };
      if (values[valueKey] === "") {
        return "";
      }

      const entryValue = values[valueKey];
      if (valueKey === "earliestReadyTime") {
        const parsedEntryValue = createComparableDate(entryValue, 3);
        const checkTime = values["latestReadyTime"]
          ? values["latestReadyTime"]
          : collectionDefaults.defaultLatestReadyTime;
        const parsedCheckTime = createComparableDate(checkTime, 0);
        if (
          !parsedEntryValue ||
          !parsedCheckTime ||
          !(parsedEntryValue <= parsedCheckTime)
        ) {
          return EARLIEST_READY_TIME_COLLECTION_ERROR;
        }
      } else if (valueKey === "latestReadyTime") {
        const parsedEntryValue = createComparableDate(entryValue, 0);
        const checkTime = values["earliestReadyTime"]
          ? values["earliestReadyTime"]
          : collectionDefaults.defaultEarliestReadyTime;
        const parsedCheckTime = createComparableDate(checkTime, 3);
        if (
          !parsedEntryValue ||
          !parsedCheckTime ||
          !(parsedEntryValue >= parsedCheckTime)
        ) {
          return LATEST_READY_TIME_COLLECTION_ERROR;
        }
      }
      return "";
    };

    const processInput = (valueKey, type) => {
      const validFormat = isValidInputFormat(valueKey, type);
      if (!validFormat) {
        return { error: true, errorMessage: INVALID_TIME_FORMAT };
      }
      const validTimeRangeMessage = validateTimeRange(valueKey);
      if (validTimeRangeMessage !== "") {
        return { error: true, errorMessage: validTimeRangeMessage };
      }
      return { error: false };
    };

    return (
      <Grid
        container
        direction={"column"}
        spacing={2}
        className={styles.container}
      >
        <Grid item>
          Enter Times Below For:{" "}
          <span className={styles.bold}>{collectionEditPostcode}</span>
        </Grid>
        <Grid container item direction={"row"} spacing={2}>
          {COLLECTION_FIELDS.map(({ text, type, valueKey }, index) => {
            const validFormat = isValidInputFormat(valueKey, type);
            const { error, errorMessage } = processInput(valueKey, type);
            if (error) {
              if (inputErrors[valueKey] === false) {
                const updatedInputErrors = { ...inputErrors };
                updatedInputErrors[valueKey] = true;
                setInputErrors(updatedInputErrors);
              }
            } else {
              if (inputErrors[valueKey] === true) {
                const updatedInputErrors = { ...inputErrors };
                updatedInputErrors[valueKey] = false;
                setInputErrors(updatedInputErrors);
              }
            }
            if (type === "text") {
              return (
                <Grid item xs={3} key={index}>
                  <InputMask
                    mask="99:99"
                    value={values[valueKey]}
                    onChange={onChange}
                  >
                    {() => (
                      <TextField
                        variant={"outlined"}
                        label={text}
                        name={valueKey}
                        error={error}
                        helperText={errorMessage}
                      />
                    )}
                  </InputMask>
                </Grid>
              );
            }
            if (type === "bool") {
              return (
                <Grid item xs={3} key={index}>
                  <FormControl
                    fullWidth
                    error={!validFormat}
                    variant={"outlined"}
                  >
                    <InputLabel id="saturn-label">{text}</InputLabel>
                    <Select
                      label={text}
                      labelId="saturn-label"
                      value={values[valueKey]}
                      onChange={onChange}
                      name={valueKey}
                    >
                      <MenuItem value={false}>No</MenuItem>
                      <MenuItem value={true}>Yes</MenuItem>
                    </Select>
                    {!validFormat && (
                      <FormHelperText>{EDIT_PLAN_SATURN_AUTO}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              );
            }
            return null;
          })}
        </Grid>
      </Grid>
    );
  }
);
